import { useEffect, useRef, useCallback, useState } from "react";
import { useEditor, clamp, isImageAsset, getFadeGain } from "./store";

/**
 * ===== Preview Engine =====
 * 
 * Hybrid preview engine with two backends:
 * 1. MediaElement (default) - Uses HTMLVideoElement, works everywhere
 * 2. WebCodecs (opt-in) - Hardware-accelerated, better scrubbing
 * 
 * WebCodecs is available but disabled by default because:
 * - Not all video formats are supported by WebCodecs
 * - Demuxing can fail for some container formats
 * - MediaElement is more reliable for general use
 * 
 * To enable WebCodecs: usePreviewEngine({ preferWebCodecs: true })
 */

// Check if WebCodecs is available in browser
function isWebCodecsSupported() {
  if (typeof window === 'undefined') return false;
  return typeof VideoDecoder !== 'undefined' && 
         typeof AudioDecoder !== 'undefined' &&
         typeof VideoFrame !== 'undefined';
}

const WEBCODECS_AVAILABLE = typeof window !== 'undefined' && isWebCodecsSupported();

const OUTPUT_WIDTH = 1920;
const OUTPUT_HEIGHT = 1080;

/**
 * Calculate draw rect for media, applying clip transform when present.
 * Transform values (mediaWidth/Height/PosX/PosY) are always stored in
 * output resolution (1920x1080). This function scales them to whatever
 * canvas size is actually being drawn to (e.g. 960x540 for preview).
 */
function getMediaDrawRect(sourceWidth, sourceHeight, canvasW, canvasH, clip) {
  const hasTransform = clip && (clip.mediaWidth != null || clip.mediaHeight != null || clip.mediaPosX != null || clip.mediaPosY != null);

  if (hasTransform) {
    const sx = canvasW / OUTPUT_WIDTH;
    const sy = canvasH / OUTPUT_HEIGHT;
    const dw = (clip.mediaWidth ?? OUTPUT_WIDTH) * sx;
    const dh = (clip.mediaHeight ?? OUTPUT_HEIGHT) * sy;
    const dx = (clip.mediaPosX ?? 0) * sx + (canvasW - dw) / 2;
    const dy = (clip.mediaPosY ?? 0) * sy + (canvasH - dh) / 2;
    return { dx, dy, dw, dh };
  }

  const scale = Math.min(canvasW / sourceWidth, canvasH / sourceHeight);
  const dw = sourceWidth * scale;
  const dh = sourceHeight * scale;
  const dx = (canvasW - dw) / 2;
  const dy = (canvasH - dh) / 2;
  return { dx, dy, dw, dh };
}

export function usePreviewEngine(options = {}) {
  const canvasRef = useRef(null);
  const { preferWebCodecs = true } = options;
  
  // Use MediaElement by default - it's more reliable
  // WebCodecs can be enabled explicitly when needed
  const useWebCodecs = preferWebCodecs && WEBCODECS_AVAILABLE;
  
  if (useWebCodecs) {
    console.log("Using WebCodecs engine");
    // WebCodecs engine with fallback on failure
    return useWebCodecsEngineWithFallback(canvasRef);
  } else {
    console.log("Using MediaElement engine");
    return useMediaElementEngine(canvasRef);
  }
}

/**
 * ========================================
 * WebCodecs Engine with Automatic Fallback
 * ========================================
 * 
 * Strategy: Use MediaElement immediately for display, while WebCodecs
 * initializes in the background. Once WebCodecs is ready, switch to it
 * for better scrubbing performance.
 * 
 * IMPORTANT: MediaElements are keyed by CLIP ID (not asset ID) so that
 * multiple clips using the same asset can have independent volume control.
 */
function useWebCodecsEngineWithFallback(canvasRef) {
  const [webCodecsFailed, setWebCodecsFailed] = useState(false);
  const videoDecoderRef = useRef(null);
  const audioDecoderRef = useRef(null);
  const initializingRef = useRef(new Set());
  const readyAssetsRef = useRef(new Set()); // Assets where WebCodecs is ready
  const failedAssetsRef = useRef(new Set()); // Assets where WebCodecs failed
  
  // MediaElements keyed by CLIP ID for independent volume control
  const mediaPoolRef = useRef(new Map()); // clipId -> HTMLMediaElement
  
  // Audio metering for WebCodecs engine
  const audioCtxRef = useRef(null);
  const meterNodesRef = useRef(null);
  const audioSourcesRef = useRef(new Map()); // clipId -> { source, gain }
  
  const assets = useEditor((s) => s.assets);
  const clips = useEditor((s) => s.clips);
  const playhead = useEditor((s) => s.playhead);
  const previewTime = useEditor((s) => s.previewTime);
  const isPlaying = useEditor((s) => s.isPlaying);
  const setAudioMeterNodes = useEditor((s) => s.setAudioMeterNodes);

  // If WebCodecs completely failed, delegate to MediaElement
  if (webCodecsFailed) {
    console.log("[WebCodecs] Completely failed, using MediaElement");
    return useMediaElementEngine(canvasRef);
  }

  // Initialize decoder managers
  useEffect(() => {
    let isMounted = true;
    
    (async () => {
      try {
        console.log("[WebCodecs] Loading decoder managers...");
        const { VideoDecoderManager, AudioDecoderManager } = await import('./webcodecs');
        if (!isMounted) return;
        
        videoDecoderRef.current = new VideoDecoderManager();
        audioDecoderRef.current = new AudioDecoderManager();
        console.log("[WebCodecs] Decoder managers ready");
        
        // Init audio on user interaction
        const initAudio = () => {
          audioDecoderRef.current?.initAudioContext().catch(console.warn);
        };
        document.addEventListener('click', initAudio, { once: true });
      } catch (e) {
        console.warn('[WebCodecs] Failed to initialize, falling back to MediaElement:', e);
        if (isMounted) setWebCodecsFailed(true);
      }
    })();
    
    return () => {
      isMounted = false;
      videoDecoderRef.current?.destroy();
      audioDecoderRef.current?.destroy();
    };
  }, []);

  // Helper to get or create MediaElement for a clip
  const getOrCreateClipMedia = useCallback((clipId, assetId) => {
    // Check if we have a cached media element for this clip
    if (mediaPoolRef.current.has(clipId)) {
      const cachedMedia = mediaPoolRef.current.get(clipId);
      const asset = assets[assetId];
      const assetUrlChanged = asset && asset.url && cachedMedia._assetUrl !== asset.url;
      if (cachedMedia._assetId !== assetId || assetUrlChanged) {
        if (!cachedMedia._isImage) {
          cachedMedia.pause();
          cachedMedia.src = '';
        }
        mediaPoolRef.current.delete(clipId);
      } else {
        return cachedMedia;
      }
    }
    
    const asset = assets[assetId];
    if (!asset) return null;
    
    // Handle images - use HTMLImageElement
    if (isImageAsset(asset)) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = asset.url;
      img._clipId = clipId;
      img._assetId = assetId;
      img._assetUrl = asset.url;
      img._isImage = true;
      mediaPoolRef.current.set(clipId, img);
      return img;
    }
    
    const isAudioAsset = asset.type === "audio";
    const el = document.createElement(isAudioAsset ? "audio" : "video");
    el.crossOrigin = "anonymous";
    el.preload = "auto";
    el.muted = false;
    el.playsInline = true;
    el.loop = false;
    el.src = asset.url;
    el._clipId = clipId;
    el._assetId = assetId;
    el._assetUrl = asset.url;
    
    mediaPoolRef.current.set(clipId, el);
    return el;
  }, [assets]);

  // Initialize clips - create MediaElement for each clip
  useEffect(() => {
    const { clips } = useEditor.getState();
    
    // Create or refresh MediaElements for all clips
    for (const clip of clips) {
      getOrCreateClipMedia(clip.id, clip.assetId);
    }
    
    // Clean up MediaElements for removed clips
    const clipIds = new Set(clips.map(c => c.id));
    for (const [clipId, media] of mediaPoolRef.current.entries()) {
      if (!clipIds.has(clipId)) {
        // Images don't have pause/src - just remove from pool
        if (!media._isImage) {
          media.pause();
          media.src = '';
        }
        mediaPoolRef.current.delete(clipId);
      }
    }
    
    // Initialize WebCodecs for video assets only (not images or audio)
    for (const [assetId, asset] of Object.entries(assets)) {
      // Skip images - they don't need WebCodecs decoding
      if (isImageAsset(asset)) continue;
      
      // Skip audio-only assets - mp4box.js can only demux MP4/M4A containers,
      // not raw audio formats like MP3/WAV/OGG. MediaElement handles audio fine.
      if (asset.type === 'audio') continue;
      
      const videoDecoder = videoDecoderRef.current;
      if (!videoDecoder) continue;
      if (initializingRef.current.has(assetId)) continue;
      if (readyAssetsRef.current.has(assetId)) continue;
      if (failedAssetsRef.current.has(assetId)) continue;
      
      initializingRef.current.add(assetId);
      
      (async () => {
        try {
          console.log(`[WebCodecs] Initializing asset: ${assetId}`);
          
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Initialization timeout (30s)')), 30000)
          );
          
          await Promise.race([
            videoDecoder.initAsset(assetId, asset.url),
            timeoutPromise
          ]);
          
          readyAssetsRef.current.add(assetId);
          console.log(`[WebCodecs] ✓ Asset ready: ${assetId}`);
        } catch (e) {
          console.warn(`[WebCodecs] ✗ Failed to init ${assetId}:`, e.message);
          failedAssetsRef.current.add(assetId);
        } finally {
          initializingRef.current.delete(assetId);
        }
      })();
    }
  }, [assets, clips, getOrCreateClipMedia]);

  // Get topmost video clip at time t
  const getTopVideoClipAt = useCallback((t) => {
    const { tracks, clips } = useEditor.getState();
    const trackIndex = (trackId) => tracks.findIndex((tr) => tr.id === trackId);

    const candidates = clips.filter((c) => {
      const tr = tracks.find((x) => x.id === c.trackId);
      if (!tr || tr.type !== "video") return false;
      return t >= c.start && t < c.start + c.duration;
    });

    candidates.sort((a, b) => trackIndex(a.trackId) - trackIndex(b.trackId));
    return candidates[0] || null;
  }, []);

  // Draw frame
  const drawFrame = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const { playhead, previewTime, assets, isPlaying } = useEditor.getState();
    const currentPlayhead = previewTime !== null ? previewTime : playhead;

    const ctx = canvas.getContext("2d");
    const w = canvas.width;
    const h = canvas.height;

    const clip = getTopVideoClipAt(currentPlayhead);
    
    if (!clip) {
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, w, h);
      return;
    }

    const asset = assets[clip.assetId];
    if (!asset) {
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, w, h);
      return;
    }

    if (isImageAsset(asset)) {
      const media = mediaPoolRef.current.get(clip.id) || getOrCreateClipMedia(clip.id, clip.assetId);
      if (media && media._isImage && media.complete && media.naturalWidth > 0) {
        const { dx, dy, dw, dh } = getMediaDrawRect(media.naturalWidth, media.naturalHeight, w, h, clip);
        ctx.fillStyle = "#000";
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(media, dx, dy, dw, dh);
      } else {
        ctx.fillStyle = "#000";
        ctx.fillRect(0, 0, w, h);
        ctx.fillStyle = "#666";
        ctx.font = "14px monospace";
        ctx.textAlign = "center";
        ctx.fillText("Loading image...", w / 2, h / 2);
      }
      return;
    }

    const assetTime = clip.in + (currentPlayhead - clip.start);
    const clampedAssetTime = clamp(assetTime, 0, (asset.duration || Infinity) - 0.001);

    // Strategy: Use MediaElement unless WebCodecs is fully ready for this asset
    const webCodecsReady = readyAssetsRef.current.has(clip.assetId);
    const videoDecoder = videoDecoderRef.current;
    
    if (webCodecsReady && videoDecoder?.decoders.has(clip.assetId)) {
      try {
        const frame = await videoDecoder.getFrame(clip.assetId, clampedAssetTime);
        if (frame) {
          const vw = frame.displayWidth || frame.codedWidth;
          const vh = frame.displayHeight || frame.codedHeight;
          const { dx, dy, dw, dh } = getMediaDrawRect(vw, vh, w, h, clip);
          
          ctx.fillStyle = "#000";
          ctx.fillRect(0, 0, w, h);
          ctx.drawImage(frame, dx, dy, dw, dh);
          return;
        }
      } catch (e) {
        console.warn(`[WebCodecs] Frame decode failed for ${clip.assetId}:`, e.message);
      }
    }

    // Use MediaElement (keyed by clip ID for independent volume control)
    const media = mediaPoolRef.current.get(clip.id);
    if (media) {
      drawMediaElement(ctx, media, clampedAssetTime, w, h, isPlaying, clip);
    } else {
      // No media element yet - create one
      const newMedia = getOrCreateClipMedia(clip.id, clip.assetId);
      if (newMedia) {
        drawMediaElement(ctx, newMedia, clampedAssetTime, w, h, isPlaying, clip);
      } else {
        ctx.fillStyle = "#000";
        ctx.fillRect(0, 0, w, h);
      }
    }
  }, [canvasRef, getTopVideoClipAt, getOrCreateClipMedia]);

  // Get ALL clips with audio at time t (both audio tracks AND video tracks)
  const getActiveAudioClipsAt = useCallback((t) => {
    const { tracks, clips } = useEditor.getState();
    return clips.filter((c) => {
      const tr = tracks.find((x) => x.id === c.trackId);
      if (!tr) return false;
      // Both audio and video tracks contribute audio
      if (tr.type !== "audio" && tr.type !== "video") return false;
      return t >= c.start && t < c.start + c.duration;
    });
  }, []);

  // Initialize metering nodes for WebCodecs engine
  const ensureMeterNodes = useCallback(() => {
    if (meterNodesRef.current) return meterNodesRef.current;
    
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    const audioCtx = audioCtxRef.current;
    
    try {
      const splitter = audioCtx.createChannelSplitter(2);
      const merger = audioCtx.createChannelMerger(2);
      const analyserL = audioCtx.createAnalyser();
      const analyserR = audioCtx.createAnalyser();
      
      analyserL.fftSize = 2048;
      analyserR.fftSize = 2048;
      analyserL.smoothingTimeConstant = 0.8;
      analyserR.smoothingTimeConstant = 0.8;
      
      splitter.connect(analyserL, 0);
      splitter.connect(analyserR, 1);
      analyserL.connect(merger, 0, 0);
      analyserR.connect(merger, 0, 1);
      merger.connect(audioCtx.destination);
      
      meterNodesRef.current = { splitter, analyserL, analyserR, merger };
      
      setAudioMeterNodes({
        context: audioCtx,
        analyserL,
        analyserR,
        splitter,
      });
      
      return meterNodesRef.current;
    } catch (e) {
      console.warn("[WebCodecs Audio] Failed to create metering nodes:", e);
      return null;
    }
  }, [setAudioMeterNodes]);

  // Get or create audio source for a clip
  const getOrCreateAudioSource = useCallback((clipId, media) => {
    if (audioSourcesRef.current.has(clipId)) {
      return audioSourcesRef.current.get(clipId);
    }
    
    const audioCtx = audioCtxRef.current;
    const meterNodes = meterNodesRef.current;
    if (!audioCtx || !meterNodes || !media || media._isImage) return null;
    
    if (media.readyState < 2) return null;
    
    try {
      const source = audioCtx.createMediaElementSource(media);
      const gain = audioCtx.createGain();
      gain.gain.value = 1;
      
      // Handle mono audio by duplicating to both channels
      // Create a splitter to access individual channels, then merge to ensure stereo
      const channelSplitter = audioCtx.createChannelSplitter(2);
      const channelMerger = audioCtx.createChannelMerger(2);
      
      // Connect source -> splitter
      source.connect(channelSplitter);
      
      // Connect splitter outputs to merger inputs
      // For mono sources: duplicate channel 0 to both outputs so it plays on both left and right
      // For stereo sources: this will duplicate the left channel to the right (acceptable fallback)
      channelSplitter.connect(channelMerger, 0, 0); // Left channel (or mono)
      channelSplitter.connect(channelMerger, 0, 1); // Right channel (duplicated from mono/left)
      
      // Connect merger -> gain -> meter splitter
      channelMerger.connect(gain).connect(meterNodes.splitter);
      
      const bundle = { source, gain, channelSplitter, channelMerger };
      audioSourcesRef.current.set(clipId, bundle);
      return bundle;
    } catch (e) {
      if (e.name !== 'InvalidStateError') {
        console.warn("[WebCodecs Audio] Failed to create source:", e);
      }
      return null;
    }
  }, []);

  // Initialize audio context on user interaction
  useEffect(() => {
    const initAudio = async () => {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      ensureMeterNodes();
      if (audioCtxRef.current.state === "suspended") {
        await audioCtxRef.current.resume().catch(() => {});
      }
    };
    
    document.addEventListener("click", initAudio);
    document.addEventListener("keydown", initAudio);
    
    return () => {
      document.removeEventListener("click", initAudio);
      document.removeEventListener("keydown", initAudio);
    };
  }, [ensureMeterNodes]);

  // Sync all audio - ALL video/audio clips should play for audio mixing
  // But only the TOP video clip should have its video drawn (handled in drawFrame)
  // Each clip has its own MediaElement (keyed by clip.id) for independent volume control
  const syncAudio = useCallback((t, playing) => {
    const topVideoClip = getTopVideoClipAt(t);
    const allAudioClips = getActiveAudioClipsAt(t);
    
    // All clips under playhead should play for audio
    const clipsToPlay = allAudioClips;
    const clipIdsToPlay = new Set(clipsToPlay.map(c => c.id));
    const { assets, mutedTracks, trackVolumes } = useEditor.getState();
    
    // Pause all media elements that should NOT be playing
    for (const [clipId, media] of mediaPoolRef.current.entries()) {
      // Skip images - they don't have pause() method
      if (media._isImage) continue;
      if (!clipIdsToPlay.has(clipId) && !media.paused) {
        media.pause();
      }
    }
    
    // Sync clips that should be playing
    for (const clip of clipsToPlay) {
      // Get or create MediaElement for this specific clip
      let media = mediaPoolRef.current.get(clip.id);
      if (!media) {
        media = getOrCreateClipMedia(clip.id, clip.assetId);
      }
      if (!media) continue;
      
      // Skip images - they don't have audio controls
      if (media._isImage) continue;
      
      const asset = assets[clip.assetId];
      if (!asset) continue;
      
      const isTopVideo = topVideoClip && clip.id === topVideoClip.id;
      
      const isTrackMuted = mutedTracks[clip.trackId] === true;
      const trackVol = trackVolumes[clip.trackId] ?? 1;
      const baseGain = isTrackMuted ? 0 : trackVol * (clip.gain ?? 1);
      
      // Apply fade in/out
      const fadeMultiplier = getFadeGain(clip, t);
      const targetGain = baseGain * fadeMultiplier;
      
      const audioBundle = getOrCreateAudioSource(clip.id, media);
      if (audioBundle?.gain) {
        audioBundle.gain.gain.value = targetGain;
        media.volume = 1;
      } else {
        media.volume = clamp(targetGain, 0, 1);
      }
      
      // Calculate asset time
      const assetTime = clip.in + (t - clip.start);
      const actualDuration = (media.duration && Number.isFinite(media.duration)) 
        ? media.duration 
        : (asset.duration || Infinity);
      const clampedAssetTime = clamp(assetTime, clip.in, Math.min(clip.in + clip.duration, actualDuration) - 0.001);
      
      if (playing) {
        if (!media.seeking && Math.abs(media.currentTime - clampedAssetTime) > 0.15) {
          media.currentTime = clampedAssetTime;
        }
        if (media.paused && media.readyState >= 2) {
          media.play().catch(() => {});
        }
      } else {
        media.pause();
        // When scrubbing, seek all clips
        if (Math.abs(media.currentTime - clampedAssetTime) > 0.05 && !media.seeking) {
          media.currentTime = clampedAssetTime;
        }
      }
    }
  }, [getActiveAudioClipsAt, getTopVideoClipAt, getOrCreateClipMedia, getOrCreateAudioSource]);

  // Animation loop
  useEffect(() => {
    let raf = 0;
    let last = performance.now();
    let lastAudioSync = 0;

    const tick = async (now) => {
      const dt = (now - last) / 1000;
      last = now;

      const state = useEditor.getState();
      const playing = state.isPlaying;
      const currentPlayhead = state.playhead;
      const clips = state.clips;
      
      if (playing) {
        const newPlayhead = currentPlayhead + dt;
        
        // Calculate the end of the last element (maximum end time of all clips)
        const timelineEnd = clips.length > 0 
          ? Math.max(...clips.map(c => c.start + c.duration))
          : 0;
        
        // Stop playback if we've reached or passed the end
        if (newPlayhead >= timelineEnd) {
          state.setPlayhead(timelineEnd);
          state.setPlaying(false);
        } else {
          state.setPlayhead(newPlayhead);
          
          // Sync audio periodically during playback
          if (now - lastAudioSync > 100) {
            lastAudioSync = now;
            syncAudio(newPlayhead, true);
          }
        }
      }

      await drawFrame();
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [drawFrame, syncAudio]);

  // Sync audio on playhead changes when not playing (scrubbing)
  useEffect(() => {
    const currentTime = previewTime !== null ? previewTime : playhead;
    if (!isPlaying) {
      syncAudio(currentTime, false);
    }
  }, [playhead, previewTime, isPlaying, syncAudio]);

  return { canvasRef };
}

/**
 * Helper to draw from HTMLMediaElement
 * NOTE: Only handles drawing. Play/pause/volume is managed by syncAudio.
 */
function drawMediaElement(ctx, media, assetTime, w, h, isPlaying, clip = null) {
  if (!isPlaying && Number.isFinite(assetTime)) {
    if (!media.seeking) {
      if (Math.abs(media.currentTime - assetTime) > 0.05) {
        media.currentTime = assetTime;
        media._seekStartTime = performance.now();
        media._seekStuckCount = 0;
      }
    } else {
      const seekAge = media._seekStartTime ? performance.now() - media._seekStartTime : 0;
      if (seekAge > 2000) {
        media._seekStuckCount = (media._seekStuckCount || 0) + 1;
        if (media._seekStuckCount <= 3) {
          try { media.load(); } catch {}
          media.currentTime = assetTime;
          media._seekStartTime = performance.now();
        }
      }
    }
  }

  if (media.videoWidth > 0 && media.videoHeight > 0) {
    const { dx, dy, dw, dh } = getMediaDrawRect(media.videoWidth, media.videoHeight, w, h, clip);
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(media, dx, dy, dw, dh);
  } else {
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, w, h);
  }
}

/**
 * ========================================
 * MediaElement-based Preview Engine (Default)
 * ========================================
 * Reliable implementation using HTMLVideoElement
 */
function useMediaElementEngine(canvasRef) {
  const tracks = useEditor((s) => s.tracks);
  const assets = useEditor((s) => s.assets);
  const clips = useEditor((s) => s.clips);
  const playhead = useEditor((s) => s.playhead);
  const previewTime = useEditor((s) => s.previewTime);
  const isPlaying = useEditor((s) => s.isPlaying);
  const setPlayhead = useEditor((s) => s.setPlayhead);
  const setAudioMeterNodes = useEditor((s) => s.setAudioMeterNodes);

  // Media element pool: assetId -> HTMLMediaElement
  const mediaPoolRef = useRef(new Map());
  const audioCtxRef = useRef(null);
  const sourcesRef = useRef(new Map());
  const meterNodesRef = useRef(null); // { splitter, analyserL, analyserR, merger }
  const previousClipsRef = useRef(new Map()); // clipId -> assetId (to detect changes)

  // Clean up media elements for assets that no longer exist or are no longer used
  useEffect(() => {
    const pool = mediaPoolRef.current;
    const { clips } = useEditor.getState();
    const assetIds = Object.keys(assets);
    const poolAssetIds = Array.from(pool.keys());
    
    // Find assetIds that are actually being used by clips
    const activeAssetIds = new Set(clips.map(c => c.assetId).filter(Boolean));
    
    // Find assetIds in the pool that no longer exist in assets OR are not used by any clip
    const orphanedAssetIds = poolAssetIds.filter(assetId => {
      return !assets[assetId] || !activeAssetIds.has(assetId);
    });
    
    // Clean up WebCodecs decoders for orphaned assets
    for (const assetId of orphanedAssetIds) {
      if (videoDecoderRef.current) {
        videoDecoderRef.current.destroyAsset(assetId).catch(e => {
          console.warn(`Error destroying video decoder for ${assetId}:`, e);
        });
      }
      if (audioDecoderRef.current) {
        audioDecoderRef.current.destroyAsset(assetId).catch(e => {
          console.warn(`Error destroying audio decoder for ${assetId}:`, e);
        });
      }
    }
    
    // Also check for assets that exist but are no longer referenced by any clip
    // This handles the case where a clip's assetId changed
    for (const assetId of poolAssetIds) {
      if (assets[assetId] && !activeAssetIds.has(assetId)) {
        // Asset exists but is not used by any clip - stop it immediately
        const media = pool.get(assetId);
        if (media && !media._isImage) {
          try {
            media.pause();
            if (media.currentTime !== undefined) {
              media.currentTime = 0;
            }
            // Mute the gain node if it exists
            const audioBundle = sourcesRef.current.get(assetId);
            if (audioBundle?.gainNode) {
              audioBundle.gainNode.gain.value = 0;
            }
          } catch (e) {
            console.warn(`Error stopping unused asset ${assetId}:`, e);
          }
        }
        // Also clean up WebCodecs decoders for unused assets
        if (videoDecoderRef.current) {
          videoDecoderRef.current.destroyAsset(assetId).catch(e => {
            console.warn(`Error destroying unused video decoder for ${assetId}:`, e);
          });
        }
        if (audioDecoderRef.current) {
          audioDecoderRef.current.destroyAsset(assetId).catch(e => {
            console.warn(`Error destroying unused audio decoder for ${assetId}:`, e);
          });
        }
      }
    }
    
    // Clean up orphaned media elements
    for (const assetId of orphanedAssetIds) {
      const media = pool.get(assetId);
      if (media) {
        // Stop and cleanup the media element immediately
        try {
          if (media.pause) {
            media.pause();
          }
          // Set currentTime to 0 to stop playback
          if (media.currentTime !== undefined) {
            media.currentTime = 0;
          }
          if (media.src) {
            media.src = '';
          }
          // Disconnect audio sources if they exist
          const audioBundle = sourcesRef.current.get(assetId);
          if (audioBundle) {
            try {
              if (audioBundle.sourceNode) {
                audioBundle.sourceNode.disconnect();
              }
              if (audioBundle.gainNode) {
                audioBundle.gainNode.disconnect();
              }
              if (audioBundle.channelSplitter) {
                audioBundle.channelSplitter.disconnect();
              }
              if (audioBundle.channelMerger) {
                audioBundle.channelMerger.disconnect();
              }
            } catch (e) {
              // Ignore disconnect errors
            }
            sourcesRef.current.delete(assetId);
          }
          console.log(`Cleaning up media element for removed/unused asset: ${assetId}`);
        } catch (e) {
          console.warn(`Error cleaning up media element for ${assetId}:`, e);
        }
      }
      pool.delete(assetId);
    }
  }, [assets, clips]);

  function getOrCreateMedia(assetId) {
    const pool = mediaPoolRef.current;
    const { assets, clips } = useEditor.getState();
    
    // Check if asset still exists in store
    const asset = assets[assetId];
    if (!asset) {
      // Asset doesn't exist - remove from pool if cached
      if (pool.has(assetId)) {
        const oldMedia = pool.get(assetId);
        try {
          if (oldMedia.pause) oldMedia.pause();
          if (oldMedia.src) oldMedia.src = '';
        } catch (e) {}
        pool.delete(assetId);
        // Also clean up audio sources
        const audioBundle = sourcesRef.current.get(assetId);
        if (audioBundle) {
          try {
            if (audioBundle.sourceNode) audioBundle.sourceNode.disconnect();
            if (audioBundle.gainNode) audioBundle.gainNode.disconnect();
            if (audioBundle.channelSplitter) audioBundle.channelSplitter.disconnect();
            if (audioBundle.channelMerger) audioBundle.channelMerger.disconnect();
          } catch (e) {}
          sourcesRef.current.delete(assetId);
        }
      }
      return null;
    }
    
    // Check if asset is still being used by any clip
    const isAssetUsed = clips.some(c => c.assetId === assetId);
    
    // If we have cached media but asset is not used, stop it
    if (pool.has(assetId) && !isAssetUsed) {
      const cachedMedia = pool.get(assetId);
      if (!cachedMedia._isImage) {
        try {
          cachedMedia.pause();
          cachedMedia.currentTime = 0;
          const audioBundle = sourcesRef.current.get(assetId);
          if (audioBundle?.gainNode) {
            audioBundle.gainNode.gain.value = 0;
          }
        } catch (e) {
          console.warn(`Error stopping unused media for ${assetId}:`, e);
        }
      }
    }
    
    if (pool.has(assetId)) {
      const cachedMedia = pool.get(assetId);
      const assetUrlChanged = asset.url && cachedMedia._assetUrl !== asset.url;
      
      if (assetUrlChanged) {
        console.log(`Asset URL changed for ${assetId}, recreating media element. Old: ${cachedMedia._assetUrl}, New: ${asset.url}`);
        try {
          if (!cachedMedia._isImage) {
            cachedMedia.pause();
            cachedMedia.src = '';
            cachedMedia.load();
            // Disconnect audio sources
            const audioBundle = sourcesRef.current.get(assetId);
            if (audioBundle) {
              try {
                if (audioBundle.sourceNode) audioBundle.sourceNode.disconnect();
                if (audioBundle.gainNode) audioBundle.gainNode.disconnect();
                if (audioBundle.channelSplitter) audioBundle.channelSplitter.disconnect();
                if (audioBundle.channelMerger) audioBundle.channelMerger.disconnect();
              } catch (e) {}
              sourcesRef.current.delete(assetId);
            }
          }
        } catch (e) {
          console.warn(`Error cleaning up old media for ${assetId}:`, e);
        }
        pool.delete(assetId);
      } else {
        // URLs match, return cached media
        return cachedMedia;
      }
    }

    // Handle images separately - they use HTMLImageElement
    if (isImageAsset(asset)) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = asset.url;
      img._assetId = assetId;
      img._assetUrl = asset.url;
      img._isImage = true;
      pool.set(assetId, img);
      return img;
    }

    const el = document.createElement(asset.type === "video" ? "video" : "audio");
    el.crossOrigin = "anonymous";
    el.preload = "auto";
    el.muted = false;
    el.playsInline = true;
    el.controls = false;
    el.loop = false;
    el.src = asset.url;
    
    el._assetId = assetId;
    el._assetUrl = asset.url;
    el._seekStartTime = null;
    el._seekStuckCount = 0;
    
    const onDurationKnown = () => {
      if (el.duration && Number.isFinite(el.duration) && el.duration > 0) {
        useEditor.getState().updateAssetDuration(assetId, el.duration);
      }
    };
    el.addEventListener('loadedmetadata', onDurationKnown, { once: true });
    el.addEventListener('durationchange', onDurationKnown);
    
    el.addEventListener('seeking', () => {
      el._seekStartTime = performance.now();
    });
    
    el.addEventListener('seeked', () => {
      const seekDuration = el._seekStartTime ? performance.now() - el._seekStartTime : 0;
      if (seekDuration > 500) {
        console.warn(`Slow seek on ${assetId}: ${seekDuration.toFixed(0)}ms`);
      }
      el._seekStartTime = null;
      el._seekStuckCount = 0;
    });
    
    el.addEventListener('ended', (e) => {
      const vid = e.target;
      vid.pause();
      vid._hasEnded = true;
      if (vid.duration && Number.isFinite(vid.duration)) {
        vid.currentTime = Math.max(0, vid.duration - 0.001);
      }
    });

    pool.set(assetId, el);
    return el;
  }

  // Initialize metering nodes (only once per audio context)
  function ensureMeterNodes(audioCtx) {
    if (meterNodesRef.current) return meterNodesRef.current;
    
    try {
      // Create stereo splitter for L/R analysis
      const splitter = audioCtx.createChannelSplitter(2);
      const merger = audioCtx.createChannelMerger(2);
      const analyserL = audioCtx.createAnalyser();
      const analyserR = audioCtx.createAnalyser();
      
      // Configure analysers for smooth metering
      analyserL.fftSize = 2048;
      analyserR.fftSize = 2048;
      analyserL.smoothingTimeConstant = 0.8;
      analyserR.smoothingTimeConstant = 0.8;
      
      // Route: splitter -> analysers -> merger -> destination
      splitter.connect(analyserL, 0);
      splitter.connect(analyserR, 1);
      analyserL.connect(merger, 0, 0);
      analyserR.connect(merger, 0, 1);
      merger.connect(audioCtx.destination);
      
      meterNodesRef.current = { splitter, analyserL, analyserR, merger };
      
      // Share with store for AudioMeter component
      setAudioMeterNodes({
        context: audioCtx,
        analyserL,
        analyserR,
        splitter,
      });
      
      console.log("[Audio] Metering nodes initialized");
      return meterNodesRef.current;
    } catch (e) {
      console.warn("[Audio] Failed to create metering nodes:", e);
      return null;
    }
  }

  async function ensureAudioGraph(assetId) {
    const { assets } = useEditor.getState();
    const asset = assets[assetId];
    if (!asset) return null;

    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    const audioCtx = audioCtxRef.current;
    
    if (audioCtx.state !== "running") {
      try {
        await audioCtx.resume();
      } catch (e) {
        return null;
      }
    }
    
    // Ensure metering nodes exist
    const meterNodes = ensureMeterNodes(audioCtx);

    if (sourcesRef.current.has(assetId)) {
      const bundle = sourcesRef.current.get(assetId);
      if (bundle && bundle.gainNode && bundle.sourceNode) {
        return bundle;
      }
      sourcesRef.current.delete(assetId);
    }

    const media = getOrCreateMedia(assetId);
    if (!media) return null;

    if (media.readyState < 2) {
      return null;
    }

    try {
      const sourceNode = audioCtx.createMediaElementSource(media);
      const gainNode = audioCtx.createGain();
      gainNode.gain.value = 1;

      // Handle mono audio by duplicating to both channels
      // Create a splitter to access individual channels, then merge to ensure stereo
      const channelSplitter = audioCtx.createChannelSplitter(2);
      const channelMerger = audioCtx.createChannelMerger(2);
      
      // Connect source -> splitter
      sourceNode.connect(channelSplitter);
      
      // Connect splitter outputs to merger inputs
      // For mono sources: duplicate channel 0 to both outputs so it plays on both left and right
      // For stereo sources: this will duplicate the left channel to the right (acceptable fallback)
      channelSplitter.connect(channelMerger, 0, 0); // Left channel (or mono)
      channelSplitter.connect(channelMerger, 0, 1); // Right channel (duplicated from mono/left)
      
      // Route through metering nodes if available: merger -> gain -> splitter (-> analysers -> merger) -> destination
      if (meterNodes) {
        channelMerger.connect(gainNode).connect(meterNodes.splitter);
      } else {
        channelMerger.connect(gainNode).connect(audioCtx.destination);
      }

      const bundle = { sourceNode, gainNode, channelSplitter, channelMerger };
      sourcesRef.current.set(assetId, bundle);
      return bundle;
    } catch (e) {
      if (e.name === 'InvalidStateError' || e.message?.includes('already been connected')) {
        console.warn("Media element already has audio source:", assetId);
      }
      return null;
    }
  }

  function getTopVideoClipAt(t) {
    const { tracks, clips } = useEditor.getState();
    const trackIndex = (trackId) => tracks.findIndex((tr) => tr.id === trackId);

    const candidates = clips.filter((c) => {
      const tr = tracks.find((x) => x.id === c.trackId);
      if (!tr || tr.type !== "video") return false;
      return t >= c.start && t < c.start + c.duration;
    });

    candidates.sort((a, b) => trackIndex(a.trackId) - trackIndex(b.trackId));
    return candidates[0] || null;
  }

  function getActiveAudioClipsAt(t) {
    const { tracks, clips } = useEditor.getState();
    return clips.filter((c) => {
      const tr = tracks.find((x) => x.id === c.trackId);
      if (!tr) return false;
      const contributes = tr.type === "audio" || tr.type === "video";
      if (!contributes) return false;
      return t >= c.start && t < c.start + c.duration;
    });
  }

  function drawFrame() {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const { playhead, previewTime, assets, isPlaying } = useEditor.getState();
    const currentPlayhead = previewTime !== null ? previewTime : playhead;

    const ctx = canvas.getContext("2d");
    const w = canvas.width;
    const h = canvas.height;

    const clip = getTopVideoClipAt(currentPlayhead);
    
    if (!clip) {
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, w, h);
      return;
    }

    const asset = assets[clip.assetId];
    if (!asset) {
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, w, h);
      return;
    }

    const media = getOrCreateMedia(clip.assetId);
    if (!media) {
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, w, h);
      return;
    }

    if (media._isImage) {
      if (media.complete && media.naturalWidth > 0) {
        const { dx, dy, dw, dh } = getMediaDrawRect(media.naturalWidth, media.naturalHeight, w, h, clip);
        ctx.fillStyle = "#000";
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(media, dx, dy, dw, dh);
      } else {
        ctx.fillStyle = "#000";
        ctx.fillRect(0, 0, w, h);
        ctx.fillStyle = "#666";
        ctx.font = "14px monospace";
        ctx.textAlign = "center";
        ctx.fillText("Loading image...", w / 2, h / 2);
      }
      return;
    }

    // Handle video/audio assets
    const clipStartTime = clip.start;
    const clipEndTime = clip.start + clip.duration;
    const assetStartTime = clip.in;
    const assetEndTime = clip.in + clip.duration;
    
    const actualDuration = (media.duration && Number.isFinite(media.duration) && media.duration > 0) 
      ? media.duration 
      : (asset.duration || Infinity);
    
    const safeMaxTime = Math.max(0, Math.min(assetEndTime, actualDuration) - 0.001);
    
    const clampedT = clamp(currentPlayhead, clipStartTime, clipEndTime);
    const assetTime = assetStartTime + (clampedT - clipStartTime);
    const clampedAssetTime = clamp(assetTime, assetStartTime, safeMaxTime);

    if (!isPlaying) {
      if (Number.isFinite(clampedAssetTime)) {
        const seekAge = media._seekStartTime ? performance.now() - media._seekStartTime : 0;
        const isSeekStuck = media.seeking && seekAge > 2000;
        
        if (isSeekStuck) {
          media._seekStuckCount = (media._seekStuckCount || 0) + 1;
          
          if (media._seekStuckCount <= 3) {
            const targetTime = clampedAssetTime;
            media.load();
            media.currentTime = targetTime;
            media._seekStartTime = performance.now();
          }
        } else if (!media.seeking) {
          if (Math.abs(media.currentTime - clampedAssetTime) > 0.05) {
            media.currentTime = clampedAssetTime;
          }
        }
      }
      if (!media.paused) {
        media.pause();
      }
    } else {
      if (media._hasEnded || media.ended) {
        const lastFrameTime = Math.max(0, actualDuration - 0.001);
        if (Math.abs(media.currentTime - lastFrameTime) > 0.001) {
          media.currentTime = lastFrameTime;
        }
      } else {
        if (!media.seeking && Math.abs(media.currentTime - clampedAssetTime) > 0.15) {
          media.currentTime = clampedAssetTime;
        }
        if (media.paused && media.readyState >= 2) {
          media.play().catch(() => {});
        }
      }
    }

    if (media.videoWidth > 0 && media.videoHeight > 0) {
      const { dx, dy, dw, dh } = getMediaDrawRect(media.videoWidth, media.videoHeight, w, h, clip);
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(media, dx, dy, dw, dh);
    } else {
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, w, h);
      ctx.fillStyle = "#666";
      ctx.font = "14px monospace";
      ctx.textAlign = "center";
      ctx.fillText("Loading...", w / 2, h / 2);
    }
  }

  const lastSyncTimeRef = useRef(new Map());

  async function syncAudioTo(t, playing) {
    const { assets, clips: allClips } = useEditor.getState();
    
    // Detect clip assetId changes and immediately stop old media
    for (const clip of allClips) {
      const previousAssetId = previousClipsRef.current.get(clip.id);
      if (previousAssetId !== undefined && previousAssetId !== clip.assetId) {
        // Clip's assetId changed - immediately stop old media and remove from pool
        if (previousAssetId && mediaPoolRef.current.has(previousAssetId)) {
          const oldMedia = mediaPoolRef.current.get(previousAssetId);
          if (!oldMedia._isImage) {
            try {
              console.log(`Clip ${clip.id} assetId changed from ${previousAssetId} to ${clip.assetId}, stopping and removing old media`);
              // Stop playback
              oldMedia.pause();
              oldMedia.currentTime = 0;
              // Clear src to fully stop the media element
              oldMedia.src = '';
              oldMedia.load();
              
              // Disconnect ALL audio sources completely
              const oldAudioBundle = sourcesRef.current.get(previousAssetId);
              if (oldAudioBundle) {
                try {
                  // Disconnect all nodes - disconnect() without arguments disconnects all connections
                  if (oldAudioBundle.sourceNode) {
                    oldAudioBundle.sourceNode.disconnect();
                  }
                  if (oldAudioBundle.gainNode) {
                    oldAudioBundle.gainNode.disconnect();
                    oldAudioBundle.gainNode.gain.value = 0;
                  }
                  if (oldAudioBundle.channelSplitter) {
                    oldAudioBundle.channelSplitter.disconnect();
                  }
                  if (oldAudioBundle.channelMerger) {
                    oldAudioBundle.channelMerger.disconnect();
                  }
                } catch (e) {
                  console.warn(`Error disconnecting audio for ${previousAssetId}:`, e);
                }
                sourcesRef.current.delete(previousAssetId);
              }
              
              // Remove from pool to force recreation if needed
              mediaPoolRef.current.delete(previousAssetId);
            } catch (e) {
              console.warn(`Error stopping old media for clip ${clip.id}:`, e);
            }
          }
        }
        
        // Clean up WebCodecs decoders for the old assetId
        if (previousAssetId) {
          if (videoDecoderRef.current) {
            videoDecoderRef.current.destroyAsset(previousAssetId).catch(e => {
              console.warn(`Error destroying video decoder for old asset ${previousAssetId}:`, e);
            });
          }
          if (audioDecoderRef.current) {
            audioDecoderRef.current.destroyAsset(previousAssetId).catch(e => {
              console.warn(`Error destroying audio decoder for old asset ${previousAssetId}:`, e);
            });
          }
        }
      }
      // Update tracking
      previousClipsRef.current.set(clip.id, clip.assetId);
    }
    
    // Remove tracking for deleted clips
    const currentClipIds = new Set(allClips.map(c => c.id));
    for (const clipId of previousClipsRef.current.keys()) {
      if (!currentClipIds.has(clipId)) {
        previousClipsRef.current.delete(clipId);
      }
    }
    
    const active = getActiveAudioClipsAt(t);
    const topVideoClip = getTopVideoClipAt(t);

    const clipsToHandle = [...active];
    if (topVideoClip && !clipsToHandle.find((c) => c.id === topVideoClip.id)) {
      clipsToHandle.push(topVideoClip);
    }

    const activeClipIds = new Set(clipsToHandle.map(c => c.id));
    for (const [clipId, syncData] of lastSyncTimeRef.current.entries()) {
      if (!activeClipIds.has(clipId)) {
        lastSyncTimeRef.current.delete(clipId);
      }
    }
    
    // Stop all media elements that shouldn't be active
    // Get all clips to check which assets should be active (allClips already defined above)
    const activeAssetIds = new Set(clipsToHandle.map(c => c.assetId).filter(Boolean));
    
    for (const [assetId, el] of mediaPoolRef.current.entries()) {
      // Skip images - they don't have pause() method
      if (el._isImage) continue;
      
      // Check if this asset is used by any clip (not just active ones)
      const isUsedByAnyClip = allClips.some(c => c.assetId === assetId);
      const shouldBeActive = activeAssetIds.has(assetId);
      
      if (!shouldBeActive || !isUsedByAnyClip) {
        // Immediately stop and reset the media element
        try {
          el.pause();
          el._hasEnded = false;
          // Reset to beginning to ensure it doesn't continue from where it was
          el.currentTime = 0;
          // Also disconnect audio sources for inactive assets
          const audioBundle = sourcesRef.current.get(assetId);
          if (audioBundle?.gainNode) {
            audioBundle.gainNode.gain.value = 0;
          }
        } catch (e) {
          console.warn(`Error stopping media element for ${assetId}:`, e);
        }
      }
    }

    // Track which assetIds are currently being used by active clips
    const currentlyUsedAssetIds = new Set(clipsToHandle.map(c => c.assetId).filter(Boolean));
    
    // Stop any media elements that are playing but not in the currently used set
    // This catches cases where a clip's assetId just changed
    for (const [assetId, el] of mediaPoolRef.current.entries()) {
      if (el._isImage) continue;
      if (!currentlyUsedAssetIds.has(assetId)) {
        // This asset is not being used by any active clip - stop it immediately
        try {
          if (!el.paused) {
            el.pause();
          }
          el.currentTime = 0;
          const audioBundle = sourcesRef.current.get(assetId);
          if (audioBundle?.gainNode) {
            audioBundle.gainNode.gain.value = 0;
          }
        } catch (e) {
          console.warn(`Error stopping unused media ${assetId}:`, e);
        }
      }
    }

    for (const clip of clipsToHandle) {
      const asset = assets[clip.assetId];
      if (!asset) continue;
      
      // Make sure we're not using an old media element - verify the asset exists and matches
      let el = getOrCreateMedia(clip.assetId);
      if (!el) continue;
      
      // Double-check: if the media element's _assetId doesn't match, something is wrong
      if (el._assetId && el._assetId !== clip.assetId) {
        console.warn(`Media element assetId mismatch: expected ${clip.assetId}, got ${el._assetId}`);
        // Remove the wrong media element and get a new one
        mediaPoolRef.current.delete(el._assetId);
        el = getOrCreateMedia(clip.assetId);
        if (!el) continue;
      }
      
      // Skip images - they don't have audio controls
      if (el._isImage) continue;

      const { mutedTracks, trackVolumes } = useEditor.getState();
      const isTrackMuted = mutedTracks[clip.trackId] === true;
      const trackVol = trackVolumes[clip.trackId] ?? 1;
      const baseGain = isTrackMuted ? 0 : trackVol * (clip.gain ?? 1);
      
      // Apply fade in/out
      const fadeMultiplier = getFadeGain(clip, t);
      const targetGain = baseGain * fadeMultiplier;

      // Try to route through Web Audio API for metering
      // If connected, use gain node; otherwise fall back to el.volume
      const audioBundle = await ensureAudioGraph(clip.assetId);
      if (audioBundle?.gainNode) {
        audioBundle.gainNode.gain.value = targetGain;
        el.volume = 1; // Let gain node control volume
      } else {
        el.volume = targetGain;
      }

      const clipStartTime = clip.start;
      const clipEndTime = clip.start + clip.duration;
      const assetStartTime = clip.in;
      const assetEndTime = clip.in + clip.duration;
      
      const actualDuration = (el.duration && Number.isFinite(el.duration) && el.duration > 0) 
        ? el.duration 
        : (asset.duration || Infinity);
      
      const clampedT = clamp(t, clipStartTime, clipEndTime);
      const assetTime = assetStartTime + (clampedT - clipStartTime);
      
      const safeMaxAssetTime = Math.max(0, Math.min(assetEndTime, actualDuration) - 0.001);
      const clampedAssetTime = clamp(assetTime, assetStartTime, safeMaxAssetTime);
      
      const lastSync = lastSyncTimeRef.current.get(clip.id);
      const now = performance.now();
      
      if (Number.isFinite(clampedAssetTime)) {
        if (!playing) {
          el._hasEnded = false;
          const isTopVideo = topVideoClip && topVideoClip.assetId === clip.assetId;
          if (!isTopVideo && !el.seeking && Math.abs(el.currentTime - clampedAssetTime) > 0.05) {
            el.currentTime = clampedAssetTime;
          }
        } else {
          const needsInitialSync = !lastSync;
          
          if (needsInitialSync) {
            el._hasEnded = false;
            el.currentTime = clampedAssetTime;
            lastSyncTimeRef.current.set(clip.id, { time: clampedAssetTime, timestamp: now });
          } else if (!el.seeking && Math.abs(el.currentTime - clampedAssetTime) > 0.15) {
            el.currentTime = clampedAssetTime;
          }
          
          if (el._hasEnded || el.ended) {
            el.pause();
          } else if (el.paused && el.readyState >= 2) {
            el.play().catch(() => {});
          }
        }
      }

      if (!playing) {
        el.pause();
      }
    }
  }

  // Initialize audio context on user interaction
  useEffect(() => {
    const initAudioOnInteraction = async () => {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
        ensureMeterNodes(audioCtxRef.current);
      }
      if (audioCtxRef.current.state === "suspended") {
        try {
          await audioCtxRef.current.resume();
          console.log("[Audio] Context resumed");
        } catch (e) {
          console.warn("[Audio] Failed to resume context:", e);
        }
      }
    };

    document.addEventListener("click", initAudioOnInteraction);
    document.addEventListener("keydown", initAudioOnInteraction);

    return () => {
      document.removeEventListener("click", initAudioOnInteraction);
      document.removeEventListener("keydown", initAudioOnInteraction);
    };
  }, []);

  // Playback loop
  useEffect(() => {
    let raf = 0;
    let last = performance.now();
    let lastSync = 0;

    function tick(now) {
      const dt = (now - last) / 1000;
      last = now;

      const state = useEditor.getState();
      const playing = state.isPlaying;
      
      if (playing) {
        const currentPlayhead = state.playhead;
        const newPlayhead = currentPlayhead + dt;
        const clips = state.clips;
        
        // Calculate the end of the last element (maximum end time of all clips)
        const timelineEnd = clips.length > 0 
          ? Math.max(...clips.map(c => c.start + c.duration))
          : 0;
        
        // Stop playback if we've reached or passed the end
        if (newPlayhead >= timelineEnd) {
          state.setPlayhead(timelineEnd);
          state.setPlaying(false);
        } else {
          state.setPlayhead(newPlayhead);
          if (now - lastSync > 100) {
            lastSync = now;
            syncAudioTo(newPlayhead, true);
          }
        }
      }

      drawFrame();

      raf = requestAnimationFrame(tick);
    }

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const prevIsPlayingRef = useRef(isPlaying);
  useEffect(() => {
    const currentTime = previewTime !== null ? previewTime : playhead;
    
    if (!isPlaying) {
      syncAudioTo(currentTime, false);
    } else if (prevIsPlayingRef.current !== isPlaying) {
      syncAudioTo(currentTime, true);
    }
    prevIsPlayingRef.current = isPlaying;
  }, [playhead, previewTime, isPlaying, clips.length]);

  return { canvasRef };
}
